"use client";

import { useEffect, useRef } from "react";
import { apiBase } from "./api";
import { watchPreviewStream } from "./preview/watch-preview-stream";
import { useWidgetInstance } from "./widget-instance-context";
/** First reconnect delay after a dropped / ended stream; doubles per failed attempt up to the cap. */
export const RECONNECT_BASE_MS = 3_000;
export const RECONNECT_MAX_MS = 60_000;
/** A connection open this long counts as healthy: the backoff restarts and the next gap resyncs. */
export const STABLE_OPEN_MS = 10_000;

/**
 * A single `threadUpdated` frame from the visitor-level thread-status stream
 * (multi-topic threads). Persistent fields mirror
 * `VisitorThread`; `typing`/`assignment` are transient and sent only when they
 * change (`null` clears them). All fields but `threadId` are optional — apply
 * whatever is present, leave the rest of the row untouched.
 */
export interface ThreadUpdateEvent {
  threadId: string;
  status?: string | null;
  /** Slot text of a `message:<slotId>` thread (`VisitorThread.title`); absent for regular threads. */
  title?: string | null;
  /** Visitor-side unread — SET, not increment (already the fixed count). */
  unReadCount?: number | null;
  updatedAt?: string | null;
  /** Visitor-facing preview only (internal notes / SYSTEM are blanked BE-side). */
  lastMessagePreview?: string | null;
  lastMessageAt?: string | null;
  lastMessageFromVisitor?: boolean | null;
  /** Id of the last message (content frames only; absent on an older BE). An edit keeps it unchanged, so it
   *  is the key the announcement dedupe runs on. */
  lastMessageId?: string | null;
  /** The thread's LAST message was deleted (no preview, no lastMessageAt). The row's preview, peek and
   *  order are stale: refetch `visitorThreads` (see `shouldRefetchForDeletedLast`). Never announces. */
  lastMessageDeletedId?: string | null;
  /** Avatar of the last message's sender (content frames only; absent on an older BE). */
  lastMessageSenderAvatar?: string | null;
  /** Last-message sender (content-message events only — same shape as the
   *  visitorThreads query). `kind` ∈ visitor|operator|ai|bot|system; `name` is
   *  the operator display name (null for ai/bot/system/visitor). Typing /
   *  assignment / read events carry `null` here → the row keeps its last known
   *  sender. */
  lastMessageSenderKind?: string | null;
  lastMessageSenderName?: string | null;
  /** Media discriminator for a media-only last message (string of the BE
   *  `VisitorMediaType` enum: PHOTO/VIDEO/AUDIO/VOICE/FILE/GIF/STICKER), `null`
   *  for a plain-text message. Drives the typed preview label + thumbnail. */
  lastMessageMediaType?: string | null;
  /** Thumbnail URL for visual media (PHOTO/VIDEO/GIF/STICKER); `null` for
   *  AUDIO/VOICE/FILE and text. */
  lastMessageMediaThumbUrl?: string | null;
  /** Live block change (from a `kind:'blocked'` webOutbound event). `blockScope`
   *  says WHICH level: `"CONTACT"` = the whole visitor (lock everything +
   *  disable new-conversation), `"THREAD"` = just this thread. Unblock arrives
   *  as `blocked: false`. */
  blocked?: boolean | null;
  blockScope?: "THREAD" | "CONTACT" | null;
  typing?: { active: boolean; kind?: "thinking" | "searching" | "typing" } | null;
  assignment?: {
    assignedStatus: "connected" | "pending" | null;
    operator?: { name: string; avatar?: string } | null;
  } | null;
}

/**
 * A `threadAdded` frame — the server's LEAN discovery signal that a NEW thread
 * now exists for this visitor (an operator proactively messaged them via
 * `startWebConversation`, a cross-device thread, or the visitor's own first
 * message created it). It carries NO render fields (no status / preview /
 * lastMessageAt) — it's just "a thread with this `chatId` appeared". The
 * consumer dedupes on `chatId` (ignore one it already holds) and follows with a
 * `visitorThreads` refetch to pull the row's render fields. `chatId` IS the
 * thread id (the same value `threadUpdated` frames call `threadId`); the server
 * dynamically subscribes the new thread's channel right after emitting this, so
 * subsequent activity arrives as ordinary `threadUpdated` frames (no reconnect).
 */
export interface ThreadAddedEvent {
  visitorId: string;
  /** The new thread's id (= `threadUpdated`'s `threadId`). Dedupe on this. */
  chatId: string;
  /** Present only for topic threads. */
  threadKey?: string;
  conversationId?: string;
  organisationId?: string;
}

interface UseVisitorThreadsRealtimeOpts {
  token: string | null;
  /**
   * Who the token belongs to (the people id). The stream's thread set is fixed at connect, so a DIFFERENT
   * visitor (shutdown, re-identify) must reconnect; a renewal of the same visitor's token must not.
   */
  visitorId?: string | null;
  enabled?: boolean;
  /**
   * Bump to force a reconnect. The stream's thread set is fixed at connect
   * time, so after the visitor creates a NEW thread we reconnect to pick it up.
   */
  epoch?: number;
  onUpdate: (event: ThreadUpdateEvent) => void;
  /**
   * Fired for each `threadAdded` frame (a newly-created thread for this
   * visitor). Optional — callers that don't render live-added threads omit it.
   */
  onAdd?: (event: ThreadAddedEvent) => void;
  /**
   * Fired when the stream opens AGAIN (the browser reconnected after a drop, or a token renewal
   * reopened it): frames sent in between were lost, so the caller should refetch the list. Not
   * fired for the first open.
   */
  onReopen?: () => void;
}

/**
 * Opens the visitor-level SSE stream (`/api/web/realtime/threads`) and fires
 * `onUpdate` for each `threadUpdated` event — one connection per visitor,
 * JWT-scoped, fanning out this visitor's threads (bounded server-side). The
 * widget drives the thread LIST's live status (unread / preview / typing /
 * assignment) from this instead of polling.
 *
 * **Auth**: `EventSource` can't set headers, so the JWT + publicKey ride on the
 * URL query — same accommodation the per-chat stream (`useVisitorRealtime`)
 * uses; the BE's guards accept them there for SSE transports.
 *
 * **Reconnect**: the browser retries dropped connections automatically. We
 * additionally reconnect when `epoch` changes (new thread created).
 *
 * **Heartbeats**: the server sends `event: keepalive` frames — the browser's
 * default `message` listener never sees them (we only bind `threadUpdated`), so
 * they're ignored for free.
 */
export function useVisitorThreadsRealtime({
  token,
  visitorId = null,
  enabled = true,
  epoch = 0,
  onUpdate,
  onAdd,
  onReopen,
}: UseVisitorThreadsRealtimeOpts): void {
  const instance = useWidgetInstance();
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  }, [onUpdate]);
  const onAddRef = useRef(onAdd);
  useEffect(() => {
    onAddRef.current = onAdd;
  }, [onAdd]);

  const onReopenRef = useRef(onReopen);
  useEffect(() => {
    onReopenRef.current = onReopen;
  }, [onReopen]);
  // True once a stream of this hook instance has opened; every later `open` follows a gap.
  const openedBeforeRef = useRef(false);
  // The list is resynced after a gap only while armed. A stream the BE keeps ending (e.g. a visitor with
  // no threads) reopens over and over; resyncing on each reopen re-raised stale unread state every few
  // seconds. A connection that stays open `STABLE_OPEN_MS` re-arms it.
  const resyncArmedRef = useRef(true);
  // The JWT only authorises the CONNECT. A silent renew (every few seconds for a short-lived preview
  // token) must not tear the stream down: that closed and reopened it on each renew, resyncing the list
  // (stale unread popping back) and churning the BE. A reconnect after a drop reads the latest token.
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const hasToken = !!token;

  // `epoch` is a deliberate reconnect trigger — bumping it re-runs this effect
  // (tear down + reopen) to pick up a newly-created thread; it isn't read here.
  // biome-ignore lint/correctness/useExhaustiveDependencies: epoch forces a reconnect, not an unused dep
  useEffect(() => {
    if (!enabled || !hasToken) return;
    if (typeof window === "undefined") return;
    const publicKey = instance.publicKey;
    if (!publicKey) return;

    let disposed = false;
    let attempts = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let stableTimer: ReturnType<typeof setTimeout> | undefined;
    let teardownSource: () => void = () => {};

    const connect = () => {
      const token = tokenRef.current;
      if (!token) return;
      const url =
        `${apiBase()}/api/web/realtime/threads` +
        `?token=${encodeURIComponent(token)}` +
        `&publicKey=${encodeURIComponent(publicKey)}`;
      const source = new EventSource(url, { withCredentials: false });

      const handle = (event: MessageEvent) => {
        try {
          onUpdateRef.current(JSON.parse(event.data) as ThreadUpdateEvent);
        } catch {
          // One malformed frame shouldn't tear down the stream.
          // eslint-disable-next-line no-console
          console.warn("[wexio] unparseable threadUpdated frame", event.data);
        }
      };

      // `threadAdded` — a lean "new thread exists" discovery frame (see
      // `ThreadAddedEvent`). Fired independently of `threadUpdated`.
      const handleAdd = (event: MessageEvent) => {
        try {
          onAddRef.current?.(JSON.parse(event.data) as ThreadAddedEvent);
        } catch {
          // One malformed frame shouldn't tear down the stream.
          console.warn("[wexio] unparseable threadAdded frame", event.data);
        }
      };

      const handleOpen = () => {
        if (openedBeforeRef.current && resyncArmedRef.current) {
          resyncArmedRef.current = false;
          onReopenRef.current?.();
        }
        openedBeforeRef.current = true;
        clearTimeout(stableTimer);
        stableTimer = setTimeout(() => {
          attempts = 0;
          resyncArmedRef.current = true;
        }, STABLE_OPEN_MS);
      };

      // A dropped or server-ended stream: the browser would retry on a fixed ~3 s clock forever. We take
      // over with a growing backoff so a stream the BE keeps ending cannot loop. (A coded `error`
      // FRAME is a MessageEvent and is left to the preview watcher.)
      const handleError = (event: Event) => {
        if (event instanceof MessageEvent) return;
        clearTimeout(stableTimer);
        teardownSource();
        if (disposed) return;
        retryTimer = setTimeout(
          connect,
          Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempts),
        );
        attempts += 1;
      };

      source.addEventListener("open", handleOpen);
      source.addEventListener("threadUpdated", handle as EventListener);
      source.addEventListener("threadAdded", handleAdd as EventListener);
      // Registered before our error listener so it still sees a refused connect as CLOSED.
      const stopWatching = watchPreviewStream(source, instance, token);
      source.addEventListener("error", handleError);
      teardownSource = () => {
        stopWatching();
        source.removeEventListener("open", handleOpen);
        source.removeEventListener("threadUpdated", handle as EventListener);
        source.removeEventListener("threadAdded", handleAdd as EventListener);
        source.removeEventListener("error", handleError);
        source.close();
      };
    };

    connect();
    return () => {
      disposed = true;
      clearTimeout(retryTimer);
      clearTimeout(stableTimer);
      teardownSource();
    };
  }, [hasToken, visitorId, enabled, epoch, instance]);
}
